import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../logo.png';
import { Users, Search } from 'lucide-react';

export default function MisPromovidos() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [estructuras, setEstructuras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroEstructura, setFiltroEstructura] = useState('');
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [promotorDestino, setPromotorDestino] = useState('');
  const [asignando, setAsignando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [perRes, catRes, usrRes] = await Promise.all([
        api.get('/capturas/personas'),
        api.get('/estructuras/catalogos'),
        api.get('/auth/usuarios')
      ]);
      setPersonas(perRes.data.personas || []);
      setEstructuras(catRes.data.estructuras || []);
      setUsuarios(usrRes.data.usuarios || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const promotoresDisponibles = usuarios.filter(u => u.rol_nombre === 'promotor');

  // Secciones que de verdad aparecen entre los promovidos visibles para este usuario —
  // se ajusta solo segun el rol, sin necesidad de listar roles a mano
  const secciones = Array.from(
    new Map(
      personas
        .filter(p => p.seccion_id && p.numero_seccion)
        .map(p => [p.seccion_id, { id: p.seccion_id, numero_seccion: p.numero_seccion }])
    ).values()
  ).sort((a, b) => String(a.numero_seccion).localeCompare(String(b.numero_seccion), undefined, { numeric: true }));

  const personasFiltradas = personas.filter(p => {
    const q = busqueda.toLowerCase();
    const coincideBusqueda = !busqueda || `${p.nombre} ${p.apellido_paterno} ${p.apellido_materno}`.toLowerCase().includes(q) || (p.telefono_principal||'').includes(q) || (p.clave_elector||'').toLowerCase().includes(q);
    const coincideNivel = !filtroNivel || p.nivel_apoyo === filtroNivel;
    const coincideEstructura = !filtroEstructura || p.estructura_id_ganadora === filtroEstructura;
    const coincideSeccion = !filtroSeccion || String(p.seccion_id) === String(filtroSeccion);
    return coincideBusqueda && coincideNivel && coincideEstructura && coincideSeccion;
  });

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAsignar = async () => {
    if (seleccionados.length === 0 || !promotorDestino) return;
    setAsignando(true);
    try {
      const r = await api.post('/capturas/reasignar-promotor', {
        persona_ids: seleccionados,
        promotor_id: promotorDestino
      });
      setMensaje({ tipo: 'exito', texto: r.data.mensaje });
      setSeleccionados([]);
      setPromotorDestino('');
      fetchData();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al asignar promotor.' });
    } finally {
      setAsignando(false);
      setTimeout(() => setMensaje(null), 4000);
    }
  };

  const getNivelColor = (nivel) => {
    switch (nivel) {
      case 'seguro': return 'bg-green-100 text-green-800';
      case 'simpatizante': return 'bg-blue-100 text-blue-800';
      case 'indeciso': return 'bg-yellow-100 text-yellow-800';
      case 'defensa_del_voto': return 'bg-purple-100 text-purple-800';
      case 'oposicion': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando promovidos...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="text-green-600" size={28} />
            Mis Promovidos
          </h2>
          <p className="text-gray-500 text-sm">Promovidos de tu estructura — puedes asignarlos a tus promotores</p>
        </div>

        {mensaje && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {mensaje.texto}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-3 text-gray-400"/>
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, teléfono o clave..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"/>
            </div>
            <select value={filtroNivel} onChange={e => setFiltroNivel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm">
              <option value="">Todos los niveles</option>
              <option value="seguro">Seguro</option>
              <option value="defensa_del_voto">Defensa del Voto</option>
              <option value="simpatizante">Simpatizante</option>
              <option value="indeciso">Indeciso</option>
            </select>
            <select value={filtroEstructura} onChange={e => setFiltroEstructura(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm">
              <option value="">Todas las estructuras</option>
              {estructuras.map(e => <option key={e.id} value={e.id}>{e.nombre_estructura}</option>)}
            </select>
            <select value={filtroSeccion} onChange={e => setFiltroSeccion(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm">
              <option value="">Todas las secciones</option>
              {secciones.map(s => <option key={s.id} value={s.id}>{s.numero_seccion}</option>)}
            </select>
          </div>
          {(filtroNivel || filtroEstructura || filtroSeccion) && (
            <p className="text-xs text-gray-500 mt-2">
              Mostrando {personasFiltradas.length} de {personas.length} promovidos
              — <button onClick={() => { setFiltroNivel(''); setFiltroEstructura(''); setFiltroSeccion(''); }} className="text-green-600 underline">Quitar filtros</button>
            </p>
          )}
        </div>

        {seleccionados.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
            <span className="text-sm font-medium text-green-800">
              {seleccionados.length} promovido(s) seleccionado(s)
            </span>
            <select value={promotorDestino} onChange={e => setPromotorDestino(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm flex-1">
              <option value="">Seleccionar promotor destino...</option>
              {promotoresDisponibles.map(u => (
                <option key={u.id} value={u.id}>{u.nombre} {u.apellido_paterno}</option>
              ))}
            </select>
            <button onClick={handleAsignar} disabled={!promotorDestino || asignando}
              className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition disabled:opacity-50 whitespace-nowrap">
              {asignando ? 'Asignando...' : '🤝 Asignar a Promotor'}
            </button>
            <button onClick={() => setSeleccionados([])} className="text-sm text-gray-500 underline whitespace-nowrap">
              Cancelar selección
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto border border-gray-100">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600 w-10">
                  <input type="checkbox"
                    checked={personasFiltradas.length > 0 && seleccionados.length === personasFiltradas.length}
                    onChange={() => setSeleccionados(seleccionados.length === personasFiltradas.length ? [] : personasFiltradas.map(p => p.id))}
                  />
                </th>
                {['Nombre','Teléfono','Sección','Estructura','Capturado por','Nivel Apoyo'].map(h=>
                  <th key={h} className="text-left py-3 px-4 font-medium text-gray-600">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {personasFiltradas.length === 0 ? (
                <tr><td colSpan="7" className="py-8 text-center text-gray-400">No se encontraron promovidos</td></tr>
              ) : personasFiltradas.map(p => (
                <tr key={p.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <input type="checkbox" checked={seleccionados.includes(p.id)} onChange={() => toggleSeleccion(p.id)} />
                  </td>
                  <td className="py-3 px-4 font-medium">{p.nombre} {p.apellido_paterno} {p.apellido_materno}</td>
                  <td className="py-3 px-4">{p.telefono_principal}</td>
                  <td className="py-3 px-4">{p.numero_seccion || '—'}</td>
                  <td className="py-3 px-4">{p.nombre_estructura || '—'}</td>
                  <td className="py-3 px-4 text-gray-600">{p.capturado_por_nombre || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getNivelColor(p.nivel_apoyo)}`}>
                      {(p.nivel_apoyo || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>
    </div>
  );
}
